import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Download, ImageDown, RefreshCw } from "lucide-react";

// ─── Download helpers ─────────────────────────────────────────────────────────

function downloadSvg(svgEl: SVGSVGElement | null, filename: string) {
  if (!svgEl) return;
  const s = new XMLSerializer().serializeToString(svgEl);
  const blob = new Blob([s], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function downloadPng(svgEl: SVGSVGElement | null, filename: string, size = 1080) {
  if (!svgEl) return;
  const s = new XMLSerializer().serializeToString(svgEl);
  const blob = new Blob([s], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = size; c.height = size;
    c.getContext("2d")!.drawImage(img, 0, 0, size, size);
    URL.revokeObjectURL(url);
    c.toBlob((b) => {
      if (!b) return;
      const pu = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = pu; a.download = filename; a.click();
      URL.revokeObjectURL(pu);
    }, "image/png");
  };
  img.src = url;
}

// ─── Shared SVG primitives ────────────────────────────────────────────────────

const W = 1080, H = 1080, CX = W / 2, CY = H / 2;
const FONT = "system-ui, -apple-system, sans-serif";

// Wrap text into lines of maxChars
function wrapLines(text: string, maxChars = 28): string[] {
  const lines: string[] = [];
  for (const para of text.split("\n")) {
    const words = para.split(" ");
    let cur = "";
    for (const w of words) {
      if ((cur + " " + w).trim().length > maxChars && cur) {
        lines.push(cur.trim());
        cur = w;
      } else cur = (cur + " " + w).trim();
    }
    if (cur) lines.push(cur);
  }
  return lines;
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 1 — Social Proof
// ═══════════════════════════════════════════════════════════════════════════════

interface T1Data { headline: string; subline: string; quotes: { text: string; author: string }[]; accentColor: string; bgColor: string }
const D1: T1Data = {
  headline: "Aimé par ceux qui\nvalorient l'équilibre",
  subline: "Ils ont transformé leur quotidien avec OneNext",
  quotes: [
    { text: "Ça ressemble moins à une app,\nplus à un refuge.", author: "Alice, 28" },
    { text: "Simple, beau, apaisant.\nJe l'ouvre chaque matin.", author: "Marie, 34" },
    { text: "OneNext m'a aidé à construire\nune routine qui me ressemble.", author: "Paul, 31" },
    { text: "Plus de culpabilité.\nJuste de la clarté.", author: "Léa, 26" },
    { text: "Aucune pression, juste\nune vraie progression.", author: "Théo, 29" },
  ],
  accentColor: "#c084fc", bgColor: "#ffffff",
};

function T1({ d, r }: { d: T1Data; r: React.Ref<SVGSVGElement> }) {
  const avatarColors = ["#f3c677","#a78bfa","#6ee7b7","#fb923c","#60a5fa"];
  const positions = [
    { x: 140, y: 185, align: "left" as const },
    { x: 720, y: 150, align: "right" as const },
    { x: 70,  y: 490, align: "left"  as const },
    { x: 710, y: 525, align: "right" as const },
    { x: 330, y: 800, align: "left"  as const },
  ];
  const hl = d.headline.split("\n");
  return (
    <svg ref={r} width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ fontFamily: FONT }}>
      <defs><radialGradient id="t1g" cx="50%" cy="50%" r="40%"><stop offset="0%" stopColor={d.accentColor} stopOpacity=".09"/><stop offset="100%" stopColor={d.bgColor} stopOpacity="0"/></radialGradient></defs>
      <rect width={W} height={H} fill={d.bgColor}/>
      <ellipse cx={CX} cy={CY} rx={380} ry={320} fill="url(#t1g)"/>
      {positions.map((p, i) => {
        const q = d.quotes[i] ?? { text: "", author: "" };
        const qL = q.text.split("\n");
        const bW = 310, lineH = 28, bH = 38 + qL.length * lineH + 38;
        const bX = p.align === "left" ? p.x : p.x - bW;
        const tailSz = 13, r2 = 18;
        const tailX = p.align === "left" ? bX + 28 : bX + bW - 28;
        return (
          <g key={i}>
            <rect x={bX} y={p.y} width={bW} height={bH} rx={r2} fill="#1c1c1e"/>
            <polygon points={`${tailX},${p.y+bH} ${tailX - tailSz},${p.y+bH+tailSz} ${tailX + tailSz},${p.y+bH}`} fill="#1c1c1e"/>
            {qL.map((l, li) => <text key={li} x={bX+20} y={p.y+30+li*lineH} fill="#fff" fontSize={17}>{l}</text>)}
            <circle cx={p.align==="left"?bX+26:bX+bW-26} cy={p.y+bH+tailSz+22} r={20} fill={avatarColors[i%5]}/>
            <text x={p.align==="left"?bX+26:bX+bW-26} y={p.y+bH+tailSz+27} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="700">{q.author.slice(0,2).toUpperCase()}</text>
            <text x={p.align==="left"?bX+54:bX+bW-54} y={p.y+bH+tailSz+28} textAnchor={p.align==="left"?"start":"end"} fill={d.accentColor} fontSize={13} fontWeight="500">{q.author}</text>
          </g>
        );
      })}
      <text x={CX} y={CY - 56 - (hl.length-1)*34} textAnchor="middle" fill="#6b7280" fontSize={20}>{d.subline}</text>
      {hl.map((l,i)=><text key={i} x={CX} y={CY-8+i*72} textAnchor="middle" fill="#111827" fontSize={60} fontWeight="800" letterSpacing="-1">{l}</text>)}
      <polygon points={`${CX+10},${CY+38} ${CX+10},${CY+68} ${CX+34},${CY+57}`} fill={d.accentColor} opacity=".9"/>
      <text x={CX} y={H-36} textAnchor="middle" fill="#9ca3af" fontSize={15} fontWeight="600">OneNext</text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 2 — Stats Arc
// ═══════════════════════════════════════════════════════════════════════════════

interface T2Data { stat1:string; stat1Label:string; stat2:string; stat2Label:string; stat3:string; stat3Label:string; tagline:string; tags:string[]; accentColor:string }
const D2: T2Data = { stat1:"2 400+", stat1Label:"Emails traités", stat2:"98%", stat2Label:"Satisfaction", stat3:"12h", stat3Label:"Économisées / mois", tagline:"Le copilote de votre boîte mail", tags:["IA intégrée","Zéro friction","100% privé","Support inclus"], accentColor:"#22c55e" };

function T2({ d, r }: { d: T2Data; r: React.Ref<SVGSVGElement> }) {
  const arcCy=520, arcRx=420, arcRy=310;
  const avatarColors=["#fbbf24","#a78bfa","#34d399","#f87171","#60a5fa","#fb923c","#e879f9"];
  const nodeAngles=[-165,-130,-100,-80,-50,-20,15];
  const nodeLabels=["✉","📊","📅","⚡","🔒","🤖","📁"];
  const toRad=(deg:number)=>deg*Math.PI/180;
  return (
    <svg ref={r} width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ fontFamily: FONT }}>
      <defs><radialGradient id="t2g" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor={d.accentColor} stopOpacity=".06"/><stop offset="100%" stopColor="#f9fafb" stopOpacity="0"/></radialGradient></defs>
      <rect width={W} height={H} fill="#f9fafb"/>
      <ellipse cx={CX} cy={arcCy} rx={460} ry={380} fill="url(#t2g)"/>
      <ellipse cx={CX} cy={arcCy} rx={arcRx} ry={arcRy} fill="none" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="8 6"/>
      {nodeAngles.map((a,i)=>{
        const nx=CX+arcRx*Math.cos(toRad(a)), ny=arcCy+arcRy*Math.sin(toRad(a));
        const big=i===0||i===3||i===6, rr=big?34:24;
        return (<g key={i}><circle cx={nx} cy={ny} r={rr+6} fill="white" opacity=".9"/><circle cx={nx} cy={ny} r={rr} fill={avatarColors[i]} opacity=".85"/><text x={nx} y={ny+7} textAnchor="middle" fontSize={big?24:16} fill="white">{nodeLabels[i]}</text></g>);
      })}
      {[{x:195,y:305,t:"12 Nouveaux mails"},{x:790,y:248,t:"❤ Enregistré"},{x:148,y:530,t:"✓ Traité"},{x:790,y:490,t:"▶ 3 Actions"}].map((b,i)=>(
        <g key={i}><rect x={b.x-6} y={b.y-18} width={b.t.length*8+22} height={28} rx={14} fill="white" stroke="#e5e7eb" strokeWidth="1.5"/><text x={b.x+b.t.length*3.8} y={b.y} textAnchor="middle" fill="#374151" fontSize={13} fontWeight="500">{b.t}</text></g>
      ))}
      <circle cx={700} cy={560} r={22} fill={d.accentColor}/><text x={700} y={568} textAnchor="middle" fill="white" fontSize={18}>✓</text>
      {[{s:d.stat1,l:d.stat1Label,x:CX-230},{s:d.stat2,l:d.stat2Label,x:CX},{s:d.stat3,l:d.stat3Label,x:CX+230}].map((s,i)=>(
        <g key={i}><text x={s.x} y={680} textAnchor="middle" fill="#111827" fontSize={58} fontWeight="800">{s.s}</text><text x={s.x} y={716} textAnchor="middle" fill="#6b7280" fontSize={18}>{s.l}</text></g>
      ))}
      <text x={CX} y={790} textAnchor="middle" fill="#111827" fontSize={38} fontWeight="700">{d.tagline}</text>
      {(() => {
        const totalW = d.tags.reduce((s,t)=>s+t.length*10+36+14,0)-14;
        let ox = CX-totalW/2;
        return d.tags.map((tag,i)=>{
          const tw=tag.length*10+36; const el=(<g key={i}><rect x={ox} y={840} width={tw} height={38} rx={10} fill="white" stroke="#d1d5db" strokeWidth="1.5"/><text x={ox+tw/2} y={864} textAnchor="middle" fill="#374151" fontSize={15} fontWeight="500">{tag}</text></g>);
          ox+=tw+14; return el;
        });
      })()}
      <text x={CX} y={H-36} textAnchor="middle" fill="#9ca3af" fontSize={15} fontWeight="600">OneNext</text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 3 — Product Showcase
// ═══════════════════════════════════════════════════════════════════════════════

interface T3Data { appName:string; headline:string; sub:string; features:string[]; accentColor:string }
const D3: T3Data = { appName:"OneNext", headline:"Votre boîte mail,\nenfin intelligente", sub:"Triez, répondez, planifiez — sans effort.", features:["Triage IA en 1 clic","Réponses suggérées","Actions auto","Zéro distraction"], accentColor:"#3b82f6" };

function T3({ d, r }: { d: T3Data; r: React.Ref<SVGSVGElement> }) {
  const navItems=["Aujourd'hui","Actions","Mail","Projets","CRM","Chat","Bibliothèque"];
  const mailRows=[
    {from:"Sophie M.",subject:"Re: Proposition Q3",tag:"Action requise",tagColor:"#ef4444"},
    {from:"Thomas B.",subject:"Compte-rendu réunion",tag:"À lire",tagColor:"#f59e0b"},
    {from:"Newsletter",subject:"Tendances IA semaine",tag:"À archiver",tagColor:"#6b7280"},
    {from:"Sarah K.",subject:"Invitation — démo vendredi",tag:"RDV détecté",tagColor:"#8b5cf6"},
    {from:"Jean-Paul",subject:"Budget prévisionnel 2025",tag:"Action requise",tagColor:"#ef4444"},
  ];
  const hl=d.headline.split("\n");
  return (
    <svg ref={r} width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ fontFamily: FONT }}>
      <defs>
        <linearGradient id="t3bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#eff6ff"/><stop offset="100%" stopColor="#f5f3ff"/></linearGradient>
        <filter id="t3sh" x="-5%" y="-5%" width="110%" height="110%"><feDropShadow dx="0" dy="8" stdDeviation="20" floodColor="#0000001a"/></filter>
      </defs>
      <rect width={W} height={H} fill="url(#t3bg)"/>
      <text x={CX} y={88} textAnchor="middle" fill="#6b7280" fontSize={20}>{d.appName} — {d.sub}</text>
      {hl.map((l,i)=><text key={i} x={CX} y={152+i*76} textAnchor="middle" fill="#111827" fontSize={68} fontWeight="800" letterSpacing="-1.5">{l}</text>)}
      {(() => {
        const total=d.features.reduce((s,f)=>s+f.length*12+36+12,0)-12; let ox=CX-total/2;
        return d.features.map((f,i)=>{const fw=f.length*12+36;const el=(<g key={i}><rect x={ox} y={310} width={fw} height={34} rx={17} fill={d.accentColor} opacity=".12"/><circle cx={ox+17} cy={327} r={5} fill={d.accentColor}/><text x={ox+28} y={332} fill={d.accentColor} fontSize={14} fontWeight="600">{f}</text></g>);ox+=fw+12;return el;});
      })()}
      <g filter="url(#t3sh)">
        <rect x={80} y={380} width={920} height={620} rx={20} fill="white"/>
        <rect x={80} y={380} width={920} height={44} rx={20} fill="#f3f4f6"/>
        <rect x={80} y={404} width={920} height={20} fill="#f3f4f6"/>
        <circle cx={112} cy={402} r={7} fill="#ef4444" opacity=".7"/><circle cx={134} cy={402} r={7} fill="#f59e0b" opacity=".7"/><circle cx={156} cy={402} r={7} fill="#22c55e" opacity=".7"/>
        <rect x={250} y={391} width={380} height={22} rx={11} fill="#e5e7eb"/>
        <text x={440} y={407} textAnchor="middle" fill="#9ca3af" fontSize={12}>app.onenext.ai</text>
        <rect x={80} y={424} width={210} height={576} fill="#fafafa"/>
        <rect x={104} y={444} width={28} height={28} rx={7} fill={d.accentColor}/><text x={118} y={463} textAnchor="middle" fill="white" fontSize={14} fontWeight="800">O</text>
        <text x={142} y={463} fill="#111827" fontSize={16} fontWeight="700">{d.appName}</text>
        {navItems.map((item,i)=>{const active=i===2;return(<g key={i}>{active&&<rect x={84} y={490+i*42} width={202} height={36} rx={8} fill={`${d.accentColor}20`}/>}<text x={110} y={513+i*42} fill={active?d.accentColor:"#6b7280"} fontSize={14} fontWeight={active?"600":"400"}>{item}</text></g>);})}
        <rect x={290} y={424} width={710} height={576} fill="white"/>
        <text x={320} y={466} fill="#111827" fontSize={22} fontWeight="700">Ma boîte mail</text>
        <text x={320} y={488} fill="#9ca3af" fontSize={13}>Triée automatiquement par OneNext</text>
        <line x1={290} y1={500} x2={1000} y2={500} stroke="#f3f4f6" strokeWidth="1"/>
        {mailRows.map((row,i)=>{
          const ry=514+i*96;
          return (<g key={i}>
            {i===0&&<rect x={290} y={ry} width={710} height={90} fill="#f0f9ff"/>}
            <circle cx={328} cy={ry+28} r={20} fill={["#fde68a","#ddd6fe","#bbf7d0","#fecaca","#bfdbfe"][i]}/>
            <text x={328} y={ry+34} textAnchor="middle" fill="#374151" fontSize={13} fontWeight="700">{row.from.slice(0,2).toUpperCase()}</text>
            <text x={360} y={ry+22} fill="#111827" fontSize={14} fontWeight={i===0?"700":"500"}>{row.from}</text>
            <text x={360} y={ry+42} fill="#6b7280" fontSize={13}>{row.subject}</text>
            <rect x={820} y={ry+12} width={row.tag.length*8+22} height={24} rx={12} fill={`${row.tagColor}18`}/>
            <text x={826} y={ry+28} fill={row.tagColor} fontSize={12} fontWeight="600">{row.tag}</text>
            {i<mailRows.length-1&&<line x1={310} y1={ry+88} x2={990} y2={ry+88} stroke="#f3f4f6" strokeWidth="1"/>}
          </g>);
        })}
      </g>
      <text x={CX} y={H-12} textAnchor="middle" fill="#9ca3af" fontSize={15} fontWeight="600">OneNext — app.onenext.ai</text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 4 — Citation forte
// ═══════════════════════════════════════════════════════════════════════════════

interface T4Data { quote:string; author:string; role:string; accentColor:string; bgDark:boolean }
const D4: T4Data = { quote:"Votre boîte mail ne devrait pas vous coûter votre journée.", author:"Équipe OneNext", role:"Productivité & IA", accentColor:"#6366f1", bgDark:false };

function T4({ d, r }: { d: T4Data; r: React.Ref<SVGSVGElement> }) {
  const bg = d.bgDark ? "#0f172a" : "#ffffff";
  const fg = d.bgDark ? "#f1f5f9" : "#0f172a";
  const muted = d.bgDark ? "#94a3b8" : "#6b7280";
  const lines = wrapLines(d.quote, 26);
  const qH = lines.length * 86;
  const startY = CY - qH / 2;
  return (
    <svg ref={r} width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ fontFamily: FONT }}>
      <defs><radialGradient id="t4g" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor={d.accentColor} stopOpacity={d.bgDark?".15":".07"}/><stop offset="100%" stopColor={bg} stopOpacity="0"/></radialGradient></defs>
      <rect width={W} height={H} fill={bg}/>
      <ellipse cx={CX} cy={CY} rx={500} ry={480} fill="url(#t4g)"/>
      {/* Giant quote mark */}
      <text x={CX} y={startY - 30} textAnchor="middle" fill={d.accentColor} fontSize={220} fontWeight="900" opacity=".12">"</text>
      {/* Quote lines */}
      {lines.map((l,i)=>(
        <text key={i} x={CX} y={startY + i*86 + 60} textAnchor="middle" fill={fg} fontSize={72} fontWeight="700" letterSpacing="-2">{l}</text>
      ))}
      {/* Accent bar */}
      <rect x={CX-60} y={startY+qH+40} width={120} height={6} rx={3} fill={d.accentColor}/>
      {/* Author */}
      <text x={CX} y={startY+qH+90} textAnchor="middle" fill={muted} fontSize={24} fontWeight="500">{d.author}</text>
      <text x={CX} y={startY+qH+124} textAnchor="middle" fill={d.accentColor} fontSize={18} fontWeight="600" letterSpacing="2">{d.role.toUpperCase()}</text>
      <text x={CX} y={H-36} textAnchor="middle" fill={muted} fontSize={15} fontWeight="600">OneNext</text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 5 — Avant / Après
// ═══════════════════════════════════════════════════════════════════════════════

interface T5Data { beforeTitle:string; afterTitle:string; beforeItems:string[]; afterItems:string[]; accentColor:string }
const D5: T5Data = {
  beforeTitle:"Sans OneNext", afterTitle:"Avec OneNext",
  beforeItems:["100+ mails non lus chaque matin","Heures perdues à trier","Actions oubliées, relances manquées","Stress et fatigue décisionnelle","Aucune visibilité sur les priorités"],
  afterItems:["Boîte triée automatiquement","Traitement en quelques minutes","Actions extraites et planifiées","Clarté et sérénité retrouvées","Priorités claires dès le réveil"],
  accentColor:"#22c55e",
};

function T5({ d, r }: { d: T5Data; r: React.Ref<SVGSVGElement> }) {
  const panelW = 480, panelH = 720, panelY = 200;
  return (
    <svg ref={r} width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ fontFamily: FONT }}>
      <rect width={W} height={H} fill="#f8fafc"/>
      <text x={CX} y={120} textAnchor="middle" fill="#111827" fontSize={52} fontWeight="800" letterSpacing="-1">Avant · Après</text>
      <text x={CX} y={164} textAnchor="middle" fill="#6b7280" fontSize={22}>Ce que OneNext change vraiment</text>

      {/* Before panel */}
      <rect x={60} y={panelY} width={panelW} height={panelH} rx={24} fill="#fef2f2"/>
      <rect x={60} y={panelY} width={panelW} height={72} rx={24} fill="#fee2e2"/>
      <rect x={60} y={panelY+48} width={panelW} height={24} rx={0} fill="#fee2e2"/>
      <text x={60+panelW/2} y={panelY+45} textAnchor="middle" fill="#b91c1c" fontSize={26} fontWeight="700">😤 {d.beforeTitle}</text>
      {d.beforeItems.map((item,i)=>(
        <g key={i}>
          <circle cx={98} cy={panelY+112+i*100} r={16} fill="#fca5a5"/>
          <text x={98} y={panelY+118+i*100} textAnchor="middle" fill="#b91c1c" fontSize={16} fontWeight="700">✕</text>
          <text x={126} y={panelY+108+i*100} fill="#374151" fontSize={21} fontWeight="500">{item.slice(0,32)}</text>
          {item.length>32&&<text x={126} y={panelY+132+i*100} fill="#374151" fontSize={21} fontWeight="500">{item.slice(32)}</text>}
        </g>
      ))}

      {/* After panel */}
      <rect x={540} y={panelY} width={panelW} height={panelH} rx={24} fill="#f0fdf4"/>
      <rect x={540} y={panelY} width={panelW} height={72} rx={24} fill="#dcfce7"/>
      <rect x={540} y={panelY+48} width={panelW} height={24} rx={0} fill="#dcfce7"/>
      <text x={540+panelW/2} y={panelY+45} textAnchor="middle" fill="#15803d" fontSize={26} fontWeight="700">✨ {d.afterTitle}</text>
      {d.afterItems.map((item,i)=>(
        <g key={i}>
          <circle cx={578} cy={panelY+112+i*100} r={16} fill={d.accentColor}/>
          <text x={578} y={panelY+118+i*100} textAnchor="middle" fill="white" fontSize={16} fontWeight="700">✓</text>
          <text x={606} y={panelY+108+i*100} fill="#374151" fontSize={21} fontWeight="500">{item.slice(0,32)}</text>
          {item.length>32&&<text x={606} y={panelY+132+i*100} fill="#374151" fontSize={21} fontWeight="500">{item.slice(32)}</text>}
        </g>
      ))}

      {/* VS badge */}
      <circle cx={CX} cy={CY+40} r={44} fill="white" stroke="#e5e7eb" strokeWidth="3"/>
      <text x={CX} y={CY+54} textAnchor="middle" fill="#111827" fontSize={30} fontWeight="900">VS</text>
      <text x={CX} y={H-36} textAnchor="middle" fill="#9ca3af" fontSize={15} fontWeight="600">OneNext</text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 6 — Comment ça marche (3 étapes)
// ═══════════════════════════════════════════════════════════════════════════════

interface T6Data { headline:string; steps:{emoji:string;title:string;desc:string}[]; accentColor:string }
const D6: T6Data = {
  headline:"3 étapes pour reprendre le contrôle",
  steps:[
    { emoji:"🔗", title:"Connectez votre mail", desc:"Gmail, Outlook, IMAP — en 30 secondes chrono, sans partager votre mot de passe." },
    { emoji:"🤖", title:"L'IA analyse & trie", desc:"Chaque email est catégorisé, resumé et priorisé automatiquement pour vous." },
    { emoji:"⚡", title:"Agissez en 1 clic", desc:"Répondez, planifiez, archivez — OneNext vous prépare des actions prêtes à valider." },
  ],
  accentColor:"#6366f1",
};

function T6({ d, r }: { d: T6Data; r: React.Ref<SVGSVGElement> }) {
  return (
    <svg ref={r} width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ fontFamily: FONT }}>
      <defs><linearGradient id="t6bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#eef2ff"/><stop offset="100%" stopColor="#f5f3ff"/></linearGradient></defs>
      <rect width={W} height={H} fill="url(#t6bg)"/>
      {/* Header */}
      <rect x={340} y={70} width={400} height={44} rx={22} fill={d.accentColor} opacity=".12"/>
      <text x={CX} y={100} textAnchor="middle" fill={d.accentColor} fontSize={20} fontWeight="700" letterSpacing="2">COMMENT ÇA MARCHE</text>
      <text x={CX} y={194} textAnchor="middle" fill="#111827" fontSize={58} fontWeight="800" letterSpacing="-1.5">{d.headline.split(" ").slice(0,2).join(" ")}</text>
      <text x={CX} y={256} textAnchor="middle" fill="#111827" fontSize={58} fontWeight="800" letterSpacing="-1.5">{d.headline.split(" ").slice(2).join(" ")}</text>

      {/* Step cards */}
      {d.steps.map((step,i)=>{
        const cardY = 320 + i * 230;
        return (
          <g key={i}>
            <rect x={80} y={cardY} width={920} height={200} rx={20} fill="white" opacity=".9"/>
            {/* Number */}
            <circle cx={150} cy={cardY+100} r={52} fill={d.accentColor} opacity=".12"/>
            <text x={150} y={cardY+112} textAnchor="middle" fill={d.accentColor} fontSize={52} fontWeight="900">{i+1}</text>
            {/* Emoji */}
            <text x={268} y={cardY+116} fontSize={60}>{step.emoji}</text>
            {/* Connector line */}
            {i<d.steps.length-1&&<line x1={150} x2={150} y1={cardY+200} y2={cardY+230} stroke={d.accentColor} strokeWidth="3" strokeDasharray="6 4" opacity=".4"/>}
            {/* Text */}
            <text x={360} y={cardY+78} fill="#111827" fontSize={30} fontWeight="700">{step.title}</text>
            {wrapLines(step.desc,46).map((l,li)=>(
              <text key={li} x={360} y={cardY+118+li*32} fill="#6b7280" fontSize={22}>{l}</text>
            ))}
          </g>
        );
      })}
      <text x={CX} y={H-36} textAnchor="middle" fill="#9ca3af" fontSize={15} fontWeight="600">OneNext</text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 7 — Comparatif
// ═══════════════════════════════════════════════════════════════════════════════

interface T7Data { features:{label:string;us:boolean;them:boolean}[]; competitor:string; accentColor:string }
const D7: T7Data = {
  competitor:"Outils traditionnels",
  features:[
    {label:"Triage IA automatique",us:true,them:false},
    {label:"Extraction d'actions",us:true,them:false},
    {label:"Réponses suggérées",us:true,them:false},
    {label:"Résumés d'emails",us:true,them:false},
    {label:"Connexion en 30s",us:true,them:false},
    {label:"Zéro publicité",us:true,them:true},
    {label:"Interface épurée",us:true,them:false},
    {label:"Multicompte mail",us:true,them:true},
  ],
  accentColor:"#6366f1",
};

function T7({ d, r }: { d: T7Data; r: React.Ref<SVGSVGElement> }) {
  const rowH = 72, tableY = 260, colW = 340;
  const tableH = d.features.length * rowH + 60;
  return (
    <svg ref={r} width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ fontFamily: FONT }}>
      <rect width={W} height={H} fill="#f8fafc"/>
      <text x={CX} y={100} textAnchor="middle" fill="#111827" fontSize={54} fontWeight="800" letterSpacing="-1">Pourquoi OneNext ?</text>
      <text x={CX} y={154} textAnchor="middle" fill="#6b7280" fontSize={22}>La comparaison qui parle d'elle-même</text>

      {/* Table bg */}
      <rect x={80} y={tableY} width={920} height={tableH} rx={20} fill="white"/>

      {/* Header */}
      <rect x={80} y={tableY} width={920} height={60} rx={20} fill={d.accentColor}/>
      <rect x={80} y={tableY+40} width={920} height={20} fill={d.accentColor}/>
      <text x={80+colW/2} y={tableY+37} textAnchor="middle" fill="white" fontSize={20} fontWeight="500">Fonctionnalité</text>
      <text x={80+colW+colW/2} y={tableY+37} textAnchor="middle" fill="white" fontSize={22} fontWeight="700">OneNext</text>
      <text x={80+colW*2+colW/2} y={tableY+37} textAnchor="middle" fill="white" fontSize={20} fontWeight="500">{d.competitor}</text>

      {/* Column dividers */}
      <line x1={80+colW} y1={tableY} x2={80+colW} y2={tableY+tableH} stroke="#f1f5f9" strokeWidth="1.5"/>
      <line x1={80+colW*2} y1={tableY} x2={80+colW*2} y2={tableY+tableH} stroke="#f1f5f9" strokeWidth="1.5"/>

      {/* Rows */}
      {d.features.map((f,i)=>{
        const ry=tableY+60+i*rowH;
        return (
          <g key={i}>
            {i%2===1&&<rect x={80} y={ry} width={920} height={rowH} fill="#f8fafc"/>}
            <text x={100} y={ry+rowH/2+8} fill="#374151" fontSize={20} fontWeight="500">{f.label}</text>
            {/* OneNext */}
            <circle cx={80+colW+colW/2} cy={ry+rowH/2} r={18} fill={f.us?"#dcfce7":"#fee2e2"}/>
            <text x={80+colW+colW/2} y={ry+rowH/2+7} textAnchor="middle" fill={f.us?"#15803d":"#b91c1c"} fontSize={18} fontWeight="700">{f.us?"✓":"✕"}</text>
            {/* Competitor */}
            <circle cx={80+colW*2+colW/2} cy={ry+rowH/2} r={18} fill={f.them?"#dcfce7":"#fee2e2"}/>
            <text x={80+colW*2+colW/2} y={ry+rowH/2+7} textAnchor="middle" fill={f.them?"#15803d":"#b91c1c"} fontSize={18} fontWeight="700">{f.them?"✓":"✕"}</text>
            <line x1={100} y1={ry+rowH} x2={980} y2={ry+rowH} stroke="#f1f5f9" strokeWidth="1"/>
          </g>
        );
      })}

      <text x={CX} y={tableY+tableH+60} textAnchor="middle" fill={d.accentColor} fontSize={28} fontWeight="700">Essayez OneNext gratuitement →</text>
      <text x={CX} y={H-36} textAnchor="middle" fill="#9ca3af" fontSize={15} fontWeight="600">OneNext</text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 8 — Tip du jour
// ═══════════════════════════════════════════════════════════════════════════════

interface T8Data { tipNumber:string; tip:string; context:string; cta:string; accentColor:string }
const D8: T8Data = {
  tipNumber:"01",
  tip:"Traitez votre boîte mail en moins de 10 minutes par jour",
  context:"Avec OneNext, commencez par les actions prioritaires extraites automatiquement — ignorez le reste jusqu'à votre prochain créneau dédié.",
  cta:"Découvrez OneNext",
  accentColor:"#f59e0b",
};

function T8({ d, r }: { d: T8Data; r: React.Ref<SVGSVGElement> }) {
  const tipLines = wrapLines(d.tip, 24);
  const ctxLines = wrapLines(d.context, 42);
  return (
    <svg ref={r} width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ fontFamily: FONT }}>
      <defs><linearGradient id="t8bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#fffbeb"/><stop offset="100%" stopColor="#fff7ed"/></linearGradient></defs>
      <rect width={W} height={H} fill="url(#t8bg)"/>
      {/* Left accent stripe */}
      <rect x={0} y={0} width={16} height={H} fill={d.accentColor}/>
      {/* Big number bg */}
      <text x={CX} y={480} textAnchor="middle" fill={d.accentColor} fontSize={540} fontWeight="900" opacity=".06">{d.tipNumber}</text>
      {/* Tip label */}
      <rect x={80} y={80} width={240} height={48} rx={24} fill={d.accentColor}/>
      <text x={80+120} y={112} textAnchor="middle" fill="white" fontSize={20} fontWeight="700">ASTUCE #{d.tipNumber}</text>
      {/* Main tip */}
      {tipLines.map((l,i)=><text key={i} x={80} y={220+i*96} fill="#111827" fontSize={80} fontWeight="900" letterSpacing="-2">{l}</text>)}
      {/* Context */}
      <rect x={80} y={220+tipLines.length*96+40} width={4} height={ctxLines.length*38+10} rx={2} fill={d.accentColor}/>
      {ctxLines.map((l,i)=><text key={i} x={104} y={220+tipLines.length*96+78+i*38} fill="#6b7280" fontSize={26}>{l}</text>)}
      {/* CTA */}
      <rect x={80} y={880} width={d.cta.length*16+60} height={64} rx={32} fill={d.accentColor}/>
      <text x={80+(d.cta.length*16+60)/2} y={920} textAnchor="middle" fill="white" fontSize={26} fontWeight="700">{d.cta} →</text>
      <text x={W-60} y={H-36} textAnchor="end" fill="#9ca3af" fontSize={15} fontWeight="600">OneNext</text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 9 — Feature Spotlight
// ═══════════════════════════════════════════════════════════════════════════════

interface T9Data { emoji:string; featureName:string; headline:string; desc:string; bullets:string[]; accentColor:string }
const D9: T9Data = {
  emoji:"🤖",
  featureName:"TRIAGE IA",
  headline:"Votre assistant\nmail personnel",
  desc:"OneNext lit chaque email à votre place et décide pour vous : urgent, à lire, à archiver.",
  bullets:["Catégorisation automatique","Détection des actions à faire","Priorisation par impact"],
  accentColor:"#8b5cf6",
};

function T9({ d, r }: { d: T9Data; r: React.Ref<SVGSVGElement> }) {
  const hl = d.headline.split("\n");
  return (
    <svg ref={r} width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ fontFamily: FONT }}>
      <defs>
        <linearGradient id="t9bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#faf5ff"/><stop offset="100%" stopColor="#f0f9ff"/></linearGradient>
        <radialGradient id="t9glow" cx="50%" cy="35%" r="50%"><stop offset="0%" stopColor={d.accentColor} stopOpacity=".15"/><stop offset="100%" stopColor="#faf5ff" stopOpacity="0"/></radialGradient>
      </defs>
      <rect width={W} height={H} fill="url(#t9bg)"/>
      <ellipse cx={CX} cy={340} rx={480} ry={400} fill="url(#t9glow)"/>

      {/* Feature badge */}
      <rect x={CX-80} y={72} width={160} height={44} rx={22} fill={d.accentColor} opacity=".15"/>
      <text x={CX} y={101} textAnchor="middle" fill={d.accentColor} fontSize={18} fontWeight="700" letterSpacing="2">{d.featureName}</text>

      {/* Giant emoji */}
      <text x={CX} y={300} textAnchor="middle" fontSize={180}>{d.emoji}</text>

      {/* Headline */}
      {hl.map((l,i)=><text key={i} x={CX} y={390+i*84} textAnchor="middle" fill="#111827" fontSize={72} fontWeight="900" letterSpacing="-2">{l}</text>)}

      {/* Accent divider */}
      <rect x={CX-40} y={390+hl.length*84+24} width={80} height={6} rx={3} fill={d.accentColor}/>

      {/* Description */}
      {wrapLines(d.desc,38).map((l,i)=><text key={i} x={CX} y={390+hl.length*84+78+i*38} textAnchor="middle" fill="#6b7280" fontSize={26}>{l}</text>)}

      {/* Bullets */}
      {d.bullets.map((b,i)=>(
        <g key={i}>
          <circle cx={CX-200} cy={830+i*64} r={16} fill={d.accentColor}/>
          <text x={CX-200} y={836+i*64} textAnchor="middle" fill="white" fontSize={14} fontWeight="700">✓</text>
          <text x={CX-174} y={836+i*64} fill="#374151" fontSize={24} fontWeight="500">{b}</text>
        </g>
      ))}
      <text x={CX} y={H-36} textAnchor="middle" fill="#9ca3af" fontSize={15} fontWeight="600">OneNext</text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 10 — ROI / Temps économisé
// ═══════════════════════════════════════════════════════════════════════════════

interface T10Data { headline:string; timeLabel:string; timeSaved:string; unit:string; bars:{label:string;before:number;after:number}[]; accentColor:string }
const D10: T10Data = {
  headline:"Récupérez votre temps",
  timeLabel:"économisées chaque semaine",
  timeSaved:"4h30",
  unit:"en moyenne par utilisateur",
  bars:[
    {label:"Lecture mails",before:90,after:12},
    {label:"Réponses",before:60,after:18},
    {label:"Organisation",before:45,after:6},
    {label:"Recherche infos",before:40,after:8},
  ],
  accentColor:"#06b6d4",
};

function T10({ d, r }: { d: T10Data; r: React.Ref<SVGSVGElement> }) {
  const maxVal = 100;
  const barAreaW = 700, barH = 36, barGap = 56, barX = 310;
  const chartY = 380;
  return (
    <svg ref={r} width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ fontFamily: FONT }}>
      <defs><linearGradient id="t10bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#ecfeff"/><stop offset="100%" stopColor="#f0fdf4"/></linearGradient></defs>
      <rect width={W} height={H} fill="url(#t10bg)"/>

      {/* Headline */}
      <text x={CX} y={100} textAnchor="middle" fill="#111827" fontSize={58} fontWeight="900" letterSpacing="-1.5">{d.headline}</text>

      {/* Big stat */}
      <text x={CX} y={220} textAnchor="middle" fill={d.accentColor} fontSize={128} fontWeight="900" letterSpacing="-4">{d.timeSaved}</text>
      <text x={CX} y={278} textAnchor="middle" fill="#6b7280" fontSize={24}>{d.timeLabel}</text>
      <text x={CX} y={316} textAnchor="middle" fill="#9ca3af" fontSize={18}>{d.unit}</text>

      {/* Chart legend */}
      <circle cx={barX} cy={chartY-24} r={8} fill="#fca5a5"/>
      <text x={barX+14} y={chartY-18} fill="#6b7280" fontSize={18}>Avant OneNext</text>
      <circle cx={barX+210} cy={chartY-24} r={8} fill={d.accentColor}/>
      <text x={barX+224} y={chartY-18} fill="#6b7280" fontSize={18}>Avec OneNext</text>

      {/* Bars */}
      {d.bars.map((b,i)=>{
        const y = chartY + i*(barH*2+barGap);
        const beforeW = (b.before/maxVal)*barAreaW;
        const afterW = (b.after/maxVal)*barAreaW;
        return (
          <g key={i}>
            <text x={barX-14} y={y+barH/2+6} textAnchor="end" fill="#374151" fontSize={20} fontWeight="500">{b.label}</text>
            {/* Before */}
            <rect x={barX} y={y} width={beforeW} height={barH} rx={barH/2} fill="#fca5a5"/>
            <text x={barX+beforeW+10} y={y+barH/2+6} fill="#b91c1c" fontSize={18} fontWeight="600">{b.before}min</text>
            {/* After */}
            <rect x={barX} y={y+barH+8} width={afterW} height={barH} rx={barH/2} fill={d.accentColor}/>
            <text x={barX+afterW+10} y={y+barH+8+barH/2+6} fill={d.accentColor} fontSize={18} fontWeight="600">{b.after}min</text>
          </g>
        );
      })}

      {/* CTA */}
      <rect x={CX-220} y={960} width={440} height={66} rx={33} fill={d.accentColor}/>
      <text x={CX} y={1001} textAnchor="middle" fill="white" fontSize={26} fontWeight="700">Essayez OneNext gratuitement →</text>
      <text x={CX} y={H-24} textAnchor="middle" fill="#9ca3af" fontSize={14} fontWeight="600">OneNext</text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Editors
// ═══════════════════════════════════════════════════════════════════════════════

function ColorRow({ label, value, onChange }: { label:string; value:string; onChange:(v:string)=>void }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e)=>onChange(e.target.value)} className="h-9 w-14 rounded border border-input cursor-pointer"/>
        <Input value={value} onChange={(e)=>onChange(e.target.value)} className="w-32"/>
      </div>
    </div>
  );
}

function E1({ d, onChange }: { d: T1Data; onChange:(d:T1Data)=>void }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1"><Label>Titre (\\n = saut de ligne)</Label><Textarea rows={2} value={d.headline} onChange={e=>onChange({...d,headline:e.target.value})}/></div>
      <div className="space-y-1"><Label>Sous-titre</Label><Input value={d.subline} onChange={e=>onChange({...d,subline:e.target.value})}/></div>
      <ColorRow label="Couleur accent" value={d.accentColor} onChange={v=>onChange({...d,accentColor:v})}/>
      <div className="space-y-2"><Label>Témoignages</Label>{d.quotes.map((q,i)=>(
        <div key={i} className="flex gap-2 items-start">
          <span className="text-xs text-muted-foreground mt-2.5 w-4 shrink-0">{i+1}</span>
          <div className="flex-1 space-y-1">
            <Input placeholder="Texte" value={q.text} onChange={e=>{const qs=[...d.quotes];qs[i]={...qs[i],text:e.target.value};onChange({...d,quotes:qs});}}/>
            <Input placeholder="Auteur" value={q.author} onChange={e=>{const qs=[...d.quotes];qs[i]={...qs[i],author:e.target.value};onChange({...d,quotes:qs});}}/>
          </div>
        </div>
      ))}</div>
    </div>
  );
}

function E2({ d, onChange }: { d: T2Data; onChange:(d:T2Data)=>void }) {
  return (
    <div className="space-y-4">
      {([["stat1","stat1Label"],["stat2","stat2Label"],["stat3","stat3Label"]] as const).map(([sk,lk],i)=>(
        <div key={i} className="grid grid-cols-2 gap-2">
          <div className="space-y-1"><Label>Stat {i+1}</Label><Input value={d[sk]} onChange={e=>onChange({...d,[sk]:e.target.value})}/></div>
          <div className="space-y-1"><Label>Label</Label><Input value={d[lk]} onChange={e=>onChange({...d,[lk]:e.target.value})}/></div>
        </div>
      ))}
      <div className="space-y-1"><Label>Tagline</Label><Input value={d.tagline} onChange={e=>onChange({...d,tagline:e.target.value})}/></div>
      <div className="space-y-1"><Label>Tags (virgules)</Label><Input value={d.tags.join(", ")} onChange={e=>onChange({...d,tags:e.target.value.split(",").map(t=>t.trim())})}/></div>
      <ColorRow label="Couleur accent" value={d.accentColor} onChange={v=>onChange({...d,accentColor:v})}/>
    </div>
  );
}

function E3({ d, onChange }: { d: T3Data; onChange:(d:T3Data)=>void }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1"><Label>Titre (\\n = saut)</Label><Textarea rows={2} value={d.headline} onChange={e=>onChange({...d,headline:e.target.value})}/></div>
      <div className="space-y-1"><Label>Sous-titre</Label><Input value={d.sub} onChange={e=>onChange({...d,sub:e.target.value})}/></div>
      <div className="space-y-1"><Label>Fonctionnalités (virgules)</Label><Input value={d.features.join(", ")} onChange={e=>onChange({...d,features:e.target.value.split(",").map(f=>f.trim())})}/></div>
      <ColorRow label="Couleur accent" value={d.accentColor} onChange={v=>onChange({...d,accentColor:v})}/>
    </div>
  );
}

function E4({ d, onChange }: { d: T4Data; onChange:(d:T4Data)=>void }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1"><Label>Citation</Label><Textarea rows={3} value={d.quote} onChange={e=>onChange({...d,quote:e.target.value})}/></div>
      <div className="space-y-1"><Label>Auteur</Label><Input value={d.author} onChange={e=>onChange({...d,author:e.target.value})}/></div>
      <div className="space-y-1"><Label>Rôle / Source</Label><Input value={d.role} onChange={e=>onChange({...d,role:e.target.value})}/></div>
      <ColorRow label="Couleur accent" value={d.accentColor} onChange={v=>onChange({...d,accentColor:v})}/>
      <div className="flex items-center gap-3"><input type="checkbox" id="t4dark" checked={d.bgDark} onChange={e=>onChange({...d,bgDark:e.target.checked})} className="h-4 w-4"/><Label htmlFor="t4dark">Fond sombre</Label></div>
    </div>
  );
}

function E5({ d, onChange }: { d: T5Data; onChange:(d:T5Data)=>void }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Titre Avant</Label><Input value={d.beforeTitle} onChange={e=>onChange({...d,beforeTitle:e.target.value})}/></div>
        <div className="space-y-1"><Label>Titre Après</Label><Input value={d.afterTitle} onChange={e=>onChange({...d,afterTitle:e.target.value})}/></div>
      </div>
      {[0,1,2,3,4].map(i=>(
        <div key={i} className="grid grid-cols-2 gap-2">
          <Input placeholder={`Avant ${i+1}`} value={d.beforeItems[i]??""} onChange={e=>{const a=[...d.beforeItems];a[i]=e.target.value;onChange({...d,beforeItems:a});}}/>
          <Input placeholder={`Après ${i+1}`} value={d.afterItems[i]??""} onChange={e=>{const a=[...d.afterItems];a[i]=e.target.value;onChange({...d,afterItems:a});}}/>
        </div>
      ))}
      <ColorRow label="Couleur accent" value={d.accentColor} onChange={v=>onChange({...d,accentColor:v})}/>
    </div>
  );
}

function E6({ d, onChange }: { d: T6Data; onChange:(d:T6Data)=>void }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1"><Label>Titre principal</Label><Input value={d.headline} onChange={e=>onChange({...d,headline:e.target.value})}/></div>
      {d.steps.map((s,i)=>(
        <div key={i} className="space-y-1 border rounded-lg p-3">
          <Label>Étape {i+1}</Label>
          <div className="flex gap-2">
            <Input className="w-16" value={s.emoji} onChange={e=>{const ss=[...d.steps];ss[i]={...ss[i],emoji:e.target.value};onChange({...d,steps:ss});}}/>
            <Input value={s.title} onChange={e=>{const ss=[...d.steps];ss[i]={...ss[i],title:e.target.value};onChange({...d,steps:ss});}}/>
          </div>
          <Textarea rows={2} value={s.desc} onChange={e=>{const ss=[...d.steps];ss[i]={...ss[i],desc:e.target.value};onChange({...d,steps:ss});}}/>
        </div>
      ))}
      <ColorRow label="Couleur accent" value={d.accentColor} onChange={v=>onChange({...d,accentColor:v})}/>
    </div>
  );
}

function E7({ d, onChange }: { d: T7Data; onChange:(d:T7Data)=>void }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1"><Label>Concurrent</Label><Input value={d.competitor} onChange={e=>onChange({...d,competitor:e.target.value})}/></div>
      <div className="space-y-2">
        {d.features.map((f,i)=>(
          <div key={i} className="flex items-center gap-2">
            <Input className="flex-1" value={f.label} onChange={e=>{const fs=[...d.features];fs[i]={...fs[i],label:e.target.value};onChange({...d,features:fs});}}/>
            <label className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap"><input type="checkbox" checked={f.us} onChange={e=>{const fs=[...d.features];fs[i]={...fs[i],us:e.target.checked};onChange({...d,features:fs});}}/> Nous</label>
            <label className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap"><input type="checkbox" checked={f.them} onChange={e=>{const fs=[...d.features];fs[i]={...fs[i],them:e.target.checked};onChange({...d,features:fs});}}/> Eux</label>
          </div>
        ))}
      </div>
      <ColorRow label="Couleur accent" value={d.accentColor} onChange={v=>onChange({...d,accentColor:v})}/>
    </div>
  );
}

function E8({ d, onChange }: { d: T8Data; onChange:(d:T8Data)=>void }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1"><Label>N° de l'astuce</Label><Input value={d.tipNumber} onChange={e=>onChange({...d,tipNumber:e.target.value})}/></div>
      <div className="space-y-1"><Label>Titre (court, percutant)</Label><Textarea rows={2} value={d.tip} onChange={e=>onChange({...d,tip:e.target.value})}/></div>
      <div className="space-y-1"><Label>Contexte / explication</Label><Textarea rows={3} value={d.context} onChange={e=>onChange({...d,context:e.target.value})}/></div>
      <div className="space-y-1"><Label>Bouton CTA</Label><Input value={d.cta} onChange={e=>onChange({...d,cta:e.target.value})}/></div>
      <ColorRow label="Couleur accent" value={d.accentColor} onChange={v=>onChange({...d,accentColor:v})}/>
    </div>
  );
}

function E9({ d, onChange }: { d: T9Data; onChange:(d:T9Data)=>void }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-2">
        <div className="space-y-1 col-span-1"><Label>Emoji</Label><Input value={d.emoji} onChange={e=>onChange({...d,emoji:e.target.value})}/></div>
        <div className="space-y-1 col-span-3"><Label>Badge (ex: TRIAGE IA)</Label><Input value={d.featureName} onChange={e=>onChange({...d,featureName:e.target.value})}/></div>
      </div>
      <div className="space-y-1"><Label>Titre (\\n = saut)</Label><Textarea rows={2} value={d.headline} onChange={e=>onChange({...d,headline:e.target.value})}/></div>
      <div className="space-y-1"><Label>Description</Label><Textarea rows={2} value={d.desc} onChange={e=>onChange({...d,desc:e.target.value})}/></div>
      <div className="space-y-1"><Label>Points clés (séparés par \\n)</Label><Textarea rows={3} value={d.bullets.join("\n")} onChange={e=>onChange({...d,bullets:e.target.value.split("\n")})}/></div>
      <ColorRow label="Couleur accent" value={d.accentColor} onChange={v=>onChange({...d,accentColor:v})}/>
    </div>
  );
}

function E10({ d, onChange }: { d: T10Data; onChange:(d:T10Data)=>void }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1"><Label>Titre</Label><Input value={d.headline} onChange={e=>onChange({...d,headline:e.target.value})}/></div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1"><Label>Temps économisé</Label><Input value={d.timeSaved} onChange={e=>onChange({...d,timeSaved:e.target.value})}/></div>
        <div className="space-y-1"><Label>Label du temps</Label><Input value={d.timeLabel} onChange={e=>onChange({...d,timeLabel:e.target.value})}/></div>
      </div>
      {d.bars.map((b,i)=>(
        <div key={i} className="flex gap-2 items-center">
          <Input className="flex-1" value={b.label} onChange={e=>{const bs=[...d.bars];bs[i]={...bs[i],label:e.target.value};onChange({...d,bars:bs});}}/>
          <Input className="w-20" type="number" placeholder="Avant" value={b.before} onChange={e=>{const bs=[...d.bars];bs[i]={...bs[i],before:Number(e.target.value)};onChange({...d,bars:bs});}}/>
          <Input className="w-20" type="number" placeholder="Après" value={b.after} onChange={e=>{const bs=[...d.bars];bs[i]={...bs[i],after:Number(e.target.value)};onChange({...d,bars:bs});}}/>
        </div>
      ))}
      <ColorRow label="Couleur accent" value={d.accentColor} onChange={v=>onChange({...d,accentColor:v})}/>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════════

type TID = "t1"|"t2"|"t3"|"t4"|"t5"|"t6"|"t7"|"t8"|"t9"|"t10";

const TEMPLATES: { id: TID; label: string; emoji: string; desc: string }[] = [
  { id:"t1",  emoji:"💬", label:"Preuve sociale",    desc:"Bulles de témoignages flottantes" },
  { id:"t2",  emoji:"📊", label:"Stats en arc",      desc:"Métriques clés avec arc de nœuds" },
  { id:"t3",  emoji:"🖥",  label:"Aperçu produit",    desc:"Mockup d'interface réaliste" },
  { id:"t4",  emoji:"✍️", label:"Citation forte",    desc:"Une citation, un impact maximal" },
  { id:"t5",  emoji:"⚖️", label:"Avant / Après",     desc:"Comparaison visuelle côte à côte" },
  { id:"t6",  emoji:"🔢", label:"3 étapes",          desc:"Onboarding / tunnel en 3 étapes" },
  { id:"t7",  emoji:"🏆", label:"Comparatif",        desc:"Tableau OneNext vs concurrents" },
  { id:"t8",  emoji:"💡", label:"Tip du jour",       desc:"Astuce productivité encadrée" },
  { id:"t9",  emoji:"🔦", label:"Feature spotlight", desc:"Focus sur une fonctionnalité" },
  { id:"t10", emoji:"⏱",  label:"ROI / Temps",       desc:"Temps économisé, barres avant/après" },
];

export default function PostsTab() {
  const [active, setActive] = useState<TID>("t1");
  const [d1, setD1] = useState(D1);
  const [d2, setD2] = useState(D2);
  const [d3, setD3] = useState(D3);
  const [d4, setD4] = useState(D4);
  const [d5, setD5] = useState(D5);
  const [d6, setD6] = useState(D6);
  const [d7, setD7] = useState(D7);
  const [d8, setD8] = useState(D8);
  const [d9, setD9] = useState(D9);
  const [d10, setD10] = useState(D10);

  const svgRef = useRef<SVGSVGElement>(null);
  const filename = `onenext-post-${active}`;

  const handleReset = useCallback(() => {
    const map: Record<TID, ()=>void> = { t1:()=>setD1(D1), t2:()=>setD2(D2), t3:()=>setD3(D3), t4:()=>setD4(D4), t5:()=>setD5(D5), t6:()=>setD6(D6), t7:()=>setD7(D7), t8:()=>setD8(D8), t9:()=>setD9(D9), t10:()=>setD10(D10) };
    map[active]();
  }, [active]);

  const tpl = TEMPLATES.find(t=>t.id===active)!;

  const Preview = () => {
    if (active==="t1") return <T1 d={d1} r={svgRef}/>;
    if (active==="t2") return <T2 d={d2} r={svgRef}/>;
    if (active==="t3") return <T3 d={d3} r={svgRef}/>;
    if (active==="t4") return <T4 d={d4} r={svgRef}/>;
    if (active==="t5") return <T5 d={d5} r={svgRef}/>;
    if (active==="t6") return <T6 d={d6} r={svgRef}/>;
    if (active==="t7") return <T7 d={d7} r={svgRef}/>;
    if (active==="t8") return <T8 d={d8} r={svgRef}/>;
    if (active==="t9") return <T9 d={d9} r={svgRef}/>;
    return <T10 d={d10} r={svgRef}/>;
  };

  const Editor = () => {
    if (active==="t1") return <E1 d={d1} onChange={setD1}/>;
    if (active==="t2") return <E2 d={d2} onChange={setD2}/>;
    if (active==="t3") return <E3 d={d3} onChange={setD3}/>;
    if (active==="t4") return <E4 d={d4} onChange={setD4}/>;
    if (active==="t5") return <E5 d={d5} onChange={setD5}/>;
    if (active==="t6") return <E6 d={d6} onChange={setD6}/>;
    if (active==="t7") return <E7 d={d7} onChange={setD7}/>;
    if (active==="t8") return <E8 d={d8} onChange={setD8}/>;
    if (active==="t9") return <E9 d={d9} onChange={setD9}/>;
    return <E10 d={d10} onChange={setD10}/>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Générateur de posts</h2>
        <p className="text-sm text-muted-foreground mt-0.5">10 templates 1080×1080 personnalisables. Modifie les textes et télécharge en SVG ou PNG.</p>
      </div>

      {/* Template grid */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {TEMPLATES.map(t=>(
          <button key={t.id} onClick={()=>setActive(t.id)}
            className={`text-left p-3 rounded-xl border-2 transition-all ${active===t.id?"border-primary bg-primary/5":"border-border hover:border-primary/30 hover:bg-muted/40"}`}>
            <div className="text-xl mb-1">{t.emoji}</div>
            <div className="font-semibold text-xs leading-tight">{t.label}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.desc}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-6">
        {/* Preview */}
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-medium text-muted-foreground">{tpl.emoji} {tpl.label} — 1080×1080</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleReset}><RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Réinitialiser</Button>
              <Button size="sm" variant="outline" onClick={()=>downloadSvg(svgRef.current,`${filename}.svg`)}><Download className="w-3.5 h-3.5 mr-1.5"/>SVG</Button>
              <Button size="sm" onClick={()=>downloadPng(svgRef.current,`${filename}.png`)}><ImageDown className="w-3.5 h-3.5 mr-1.5"/>PNG 1080px</Button>
            </div>
          </div>
          <div className="rounded-xl border border-border overflow-hidden bg-white">
            <div className="w-full aspect-square"><Preview/></div>
          </div>
        </div>

        {/* Editor */}
        <div className="rounded-xl border border-border p-5 bg-muted/30 h-fit space-y-1 overflow-y-auto max-h-[800px]">
          <h3 className="text-sm font-semibold mb-4">Personnalisation — {tpl.label}</h3>
          <Editor/>
        </div>
      </div>
    </div>
  );
}
